/**
 * Type contracts for the Candidates table page (Batch 30c).
 *
 * `CandidateTableRow` is the joined row shape — `useCandidates()` columns
 * decorated with optional ranking columns from `useRanking({ jdId })`. See
 * design §2.2 for the join logic.
 *
 * `SortKey` / `SortDir` are the URL-driven sort axes. URL canon: `?sort=<key>&dir=<asc|desc>`.
 */
import type { CandidateStatus } from "@/components/candidates/StatusPill";
import type { FitOverall } from "@/lib/fitScore";
import type { TenureSignal } from "@/hooks/queries/candidates/useRanking";

export type SortKey = "rank" | "name" | "status" | "fit" | "tenure";
export type SortDir = "asc" | "desc";

export interface CandidateTableRow {
  // From useCandidates() — always present:
  id: string;
  name: string;
  status: CandidateStatus;
  filename: string | null;
  linkedin_url: string | null;
  is_sample: boolean;
  /** ISO 8601 timestamp of last pipeline activity; from candidates.last_activity_at. */
  last_activity_at: string;
  // From useRanking({ jdId }) — null when no JD selected, no completed run,
  // or candidate not present in the ranking response:
  rank_position: number | null;
  fit_overall: FitOverall | null;
  tenure_signal: TenureSignal | null;
  summary: string | null;
  /** ranking.system v3: stored 0–100 score; null for legacy rows. */
  fit_score: number | null;
  /**
   * JD-anchored, evidence-gated skill tags from rankings.skills_tags (≤6 per ranking).
   * Empty array when no ranking row exists for this candidate. Never null.
   */
  skills_tags: string[];
}

/** Statuses that ALWAYS sink to the bottom of the table, regardless of sort key/dir. */
export const TERMINAL_STATUSES: ReadonlySet<CandidateStatus> = new Set<CandidateStatus>([
  "opted_out",
  "rejected",
]);

/** Status enum ordering used by the `status` sort key. Terminal still sinks via §4.1. */
export const STATUS_SORT_ORDER: Record<CandidateStatus, number> = {
  active: 0,
  phone_screen: 1,
  interview_scheduled: 2,
  interviewed: 3,
  offer: 4,
  hired: 5,
  rejected: 6,
  opted_out: 7,
};

/** Fit-tier ordering for the `fit` sort key. null sorts after all tiers. */
export const FIT_TIER_ORDER: Record<FitOverall, number> = {
  strong: 0,
  moderate: 1,
  weak: 2,
};

/** Tenure-tier ordering for the `tenure` sort key. null sorts after all tiers. */
export const TENURE_TIER_ORDER: Record<TenureSignal, number> = {
  strong: 0,
  moderate: 1,
  concern: 2,
};
